import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Form, InputGroup } from 'react-bootstrap';

import {
  formatDayMonthYear,
  formatMonthYear,
  isValidPeriodInput,
  normalizePeriodInput,
  parsePeriodInput,
} from '../../utils/monthYearParse';
import './MonthYearInput.css';

const MONTH_LABELS = [
  'janeiro',
  'fevereiro',
  'março',
  'abril',
  'maio',
  'junho',
  'julho',
  'agosto',
  'setembro',
  'outubro',
  'novembro',
  'dezembro',
];

const WEEKDAYS = ['D', 'S', 'T', 'Q', 'Q', 'S', 'S'];
const POPOVER_WIDTH = 292;

interface MonthYearInputProps {
  id?: string;
  label: string;
  value: string;
  placeholder?: string;
  disabled?: boolean;
  onChange: (next: string) => void;
}

const daysInMonth = (year: number, month: number): number => new Date(year, month, 0).getDate();

const MonthYearInput: React.FC<MonthYearInputProps> = ({
  id,
  label,
  value,
  placeholder = 'dd/mm/aaaa',
  disabled = false,
  onChange,
}) => {
  const autoId = useId();
  const inputId = id || `month-year-${autoId}`;
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const popoverRef = useRef<HTMLDivElement | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [draftDay, setDraftDay] = useState<number | null>(null);
  const [draftMonth, setDraftMonth] = useState(new Date().getMonth() + 1);
  const [draftYear, setDraftYear] = useState(new Date().getFullYear());
  const [popoverStyle, setPopoverStyle] = useState<React.CSSProperties>({});

  const yearOptions = useMemo(() => {
    const current = new Date().getFullYear();
    const years: number[] = [];
    for (let year = 1990; year <= current + 5; year += 1) {
      years.push(year);
    }
    const parsed = parsePeriodInput(value);
    if (parsed && !years.includes(parsed.year)) {
      years.push(parsed.year);
      years.sort((left, right) => left - right);
    }
    return years;
  }, [value]);

  const isValid = isValidPeriodInput(value);

  const syncDraftFromValue = useCallback(() => {
    const parsedValue = parsePeriodInput(value);
    if (parsedValue) {
      setDraftMonth(parsedValue.month);
      setDraftYear(parsedValue.year);
      setDraftDay(parsedValue.day);
      return;
    }
    const now = new Date();
    setDraftMonth(now.getMonth() + 1);
    setDraftYear(now.getFullYear());
    setDraftDay(now.getDate());
  }, [value]);

  const updatePopoverPosition = useCallback(() => {
    const anchor = wrapperRef.current;
    if (!anchor) return;
    const rect = anchor.getBoundingClientRect();
    let left = rect.left;
    if (left + POPOVER_WIDTH > window.innerWidth - 8) {
      left = Math.max(8, window.innerWidth - POPOVER_WIDTH - 8);
    }
    setPopoverStyle({
      position: 'fixed',
      top: rect.bottom + 4,
      left,
      width: POPOVER_WIDTH,
      zIndex: 2000,
    });
  }, []);

  useEffect(() => {
    if (!pickerOpen) return undefined;
    syncDraftFromValue();
    updatePopoverPosition();

    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        !wrapperRef.current?.contains(target)
        && !popoverRef.current?.contains(target)
      ) {
        setPickerOpen(false);
      }
    };
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setPickerOpen(false);
    };
    const handleReposition = () => updatePopoverPosition();

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleEscape);
    window.addEventListener('resize', handleReposition);
    window.addEventListener('scroll', handleReposition, true);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleEscape);
      window.removeEventListener('resize', handleReposition);
      window.removeEventListener('scroll', handleReposition, true);
    };
  }, [pickerOpen, syncDraftFromValue, updatePopoverPosition]);

  const openPicker = () => {
    if (disabled) return;
    syncDraftFromValue();
    updatePopoverPosition();
    setPickerOpen((prev) => !prev);
  };

  const shiftMonth = (delta: number) => {
    const next = new Date(draftYear, draftMonth - 1 + delta, 1);
    setDraftYear(next.getFullYear());
    setDraftMonth(next.getMonth() + 1);
    setDraftDay(null);
  };

  const applyDay = (day: number) => {
    onChange(formatDayMonthYear(day, draftMonth, draftYear));
    setPickerOpen(false);
  };

  const applyCurrentMonth = () => {
    const now = new Date();
    onChange(formatMonthYear(now.getMonth() + 1, now.getFullYear()));
    setPickerOpen(false);
  };

  const applyToday = () => {
    const now = new Date();
    onChange(formatDayMonthYear(now.getDate(), now.getMonth() + 1, now.getFullYear()));
    setPickerOpen(false);
  };

  const clearValue = () => {
    onChange('');
    setPickerOpen(false);
  };

  const blanks = new Date(draftYear, draftMonth - 1, 1).getDay();
  const lastDay = daysInMonth(draftYear, draftMonth);
  const dayCells = [
    ...Array.from({ length: blanks }, () => null),
    ...Array.from({ length: lastDay }, (_, index) => index + 1),
  ];

  const popover = pickerOpen ? (
    <div
      ref={popoverRef}
      className="month-year-input__popover"
      style={popoverStyle}
      role="dialog"
      aria-label={label}
    >
      <div className="month-year-input__year-bar">
        <button
          type="button"
          className="month-year-input__year-nav"
          aria-label="Mês anterior"
          onClick={() => shiftMonth(-1)}
        >
          ‹
        </button>
        <span className="month-year-input__month-label">
          {MONTH_LABELS[draftMonth - 1]}
        </span>
        <Form.Select
          size="sm"
          className="month-year-input__year-select"
          value={draftYear}
          aria-label="Selecionar ano"
          onChange={(event) => {
            setDraftYear(Number(event.target.value));
            setDraftDay(null);
          }}
        >
          {yearOptions.map((year) => (
            <option key={year} value={year}>{year}</option>
          ))}
        </Form.Select>
        <button
          type="button"
          className="month-year-input__year-nav"
          aria-label="Próximo mês"
          onClick={() => shiftMonth(1)}
        >
          ›
        </button>
      </div>

      <div className="month-year-input__weekday-row">
        {WEEKDAYS.map((weekday, index) => (
          <span key={`${weekday}-${index}`} className="month-year-input__weekday">
            {weekday}
          </span>
        ))}
      </div>
      <div className="month-year-input__day-grid">
        {dayCells.map((day, index) => (
          day == null ? (
            <span key={`blank-${index}`} className="month-year-input__day-btn is-blank" />
          ) : (
            <button
              key={day}
              type="button"
              className={[
                'month-year-input__day-btn',
                draftDay === day ? 'is-active' : '',
              ].filter(Boolean).join(' ')}
              onClick={() => applyDay(day)}
            >
              {day}
            </button>
          )
        ))}
      </div>

      <div className="month-year-input__popover-footer">
        <button type="button" className="btn btn-link btn-sm p-0" onClick={clearValue}>
          Limpar
        </button>
        <button type="button" className="btn btn-link btn-sm p-0" onClick={applyToday}>
          Hoje
        </button>
        <button type="button" className="btn btn-link btn-sm p-0" onClick={applyCurrentMonth}>
          Este mês
        </button>
      </div>
    </div>
  ) : null;

  return (
    <div className="month-year-input" ref={wrapperRef}>
      <Form.Label htmlFor={inputId} className="small mb-1">
        {label}
      </Form.Label>
      <InputGroup size="sm">
        <Form.Control
          id={inputId}
          type="text"
          inputMode="numeric"
          placeholder={placeholder}
          value={value}
          disabled={disabled}
          isInvalid={!isValid}
          autoComplete="off"
          aria-describedby={`${inputId}-hint`}
          onChange={(event) => onChange(event.target.value)}
          onBlur={(event) => onChange(normalizePeriodInput(event.target.value))}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              onChange(normalizePeriodInput(value));
            }
          }}
        />
        <button
          type="button"
          className="btn btn-outline-secondary month-year-input__picker-btn"
          disabled={disabled}
          aria-label={`Abrir seletor de ${label}`}
          title="Escolher data"
          onClick={openPicker}
        >
          <svg aria-hidden="true" width="14" height="14" viewBox="0 0 16 16" fill="currentColor" focusable="false">
            <path d="M3.5 2a.5.5 0 0 0-.5.5v11a.5.5 0 0 0 .5.5h9a.5.5 0 0 0 .5-.5v-11a.5.5 0 0 0-.5-.5h-9zM3 2.5A1.5 1.5 0 0 1 4.5 1h1v.5a.5.5 0 0 0 1 0V1h3v.5a.5.5 0 0 0 1 0V1h1A1.5 1.5 0 0 1 12 2.5V3H3v-.5z" />
          </svg>
        </button>
      </InputGroup>

      {popover && createPortal(popover, document.body)}

      <Form.Text id={`${inputId}-hint`} muted className="month-year-input__hint">
        dd/mm/aaaa ou mm/aaaa. Ex.: 20/08/2026 ou 01/2025.
      </Form.Text>
      {!isValid && (
        <div className="invalid-feedback d-block">
          Use dd/mm/aaaa (ex.: 05/05/1992) ou mm/aaaa (ex.: 01/2026).
        </div>
      )}
    </div>
  );
};

export default MonthYearInput;
